import React, { useCallback, useMemo, useState } from "react";

import {
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from "@mui/material";
import EmptyCardDataOperationsSecurities from "assets/images/EmptyCardDataOperationsSecurities.png";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { InlineFilter } from "components/Filters/InlineFilter";
import { SelectDateRange } from "components/forms/Select-date-range/SelectDateRange";
import { Search } from "components/Search/Search";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { usePrompt } from "hooks/shared-hooks/use-prompt-hook";
import { useDealFiltersSelector } from "store";
import { DateRangeValue } from "store/types";
import { IDealsTableRow } from "store/types/deal/deal";
import { ISecurityTableRow } from "store/types/deal/securities";
import { ReportFilters } from "store/types/reports";

import { SecuritiesTableRow } from "./SecuritiesTableRow";
import { SecurityTransactions } from "./SecurityTransactions";
import { SelectSecurity } from "./SelectSecurity";

const HeaderCell = styled(TableCell)({
  fontSize: "14px",
  fontWeight: 500,
  color: "#071F35",
  whiteSpace: "nowrap",
  backgroundColor: "rgba(246, 248, 252, 1)",
  borderBottom: "1px solid #DBE8F5",
  padding: "6px 8px"
});

export type SecuritiesFilters = {
  dateRange: DateRangeValue;
  filters: ReportFilters;
  search: string;
};
interface SecuritiesTableProps {
  rows: ISecurityTableRow[];
  updateSecurityConsolidation: (data: { dealId: number; securityId: number; roundName?: string }) => Promise<void>;
  undoSecurityConsolidation: (securityId: number) => Promise<void>;
  filters: SecuritiesFilters;
  setFilters: React.Dispatch<React.SetStateAction<SecuritiesFilters>>;
  dealList: IDealsTableRow[];
  refetchDeals: () => void;
  onAssetClassChange: (dealId: number, assetClassId: number) => Promise<void>;
}

export const SecuritiesTable: React.FC<SecuritiesTableProps> = ({
  rows,
  filters,
  setFilters,
  updateSecurityConsolidation,
  undoSecurityConsolidation,
  dealList,
  refetchDeals,
  onAssetClassChange
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [searchValue, setSearchValue] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const openPrompt = usePrompt();
  const filtersList = useDealFiltersSelector();

  const filteredData = useMemo(() => {
    return rows.filter(
      (row) =>
        (row.security_name || "").toLowerCase().includes(searchValue.toLowerCase()) ||
        (row.deal_name || "").toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [rows, searchValue]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleToggle = useCallback((id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleSecurityClick = useCallback(
    async (row: ISecurityTableRow) => {
      await openPrompt({
        Component: SecurityTransactions,
        props: {
          deal_id: row.deal_id,
          securityId: row.security_id ?? null,
          name: row.security_name ?? row.deal_name ?? null,
          investmentEntity: row.investor_name ?? "All",
          institute: row.institution_name ?? "All"
        }
      });
    },
    [openPrompt]
  );

  const handleUndoSecurity = useCallback(
    async (row: ISecurityTableRow) => {
      if (!row.security_id) return;
      await undoSecurityConsolidation(row.security_id);
    },
    [undoSecurityConsolidation]
  );

  const assignSecurity = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>, row: ISecurityTableRow) => {
      if (!row.security_id) {
        return;
      }

      const result = await openPrompt({
        Component: SelectSecurity,
        props: {
          deal: row.deal_id,
          dealList: dealList,
          refetchDeals: refetchDeals
        },
        anchorEl: e.currentTarget,
        componentType: "popover"
      });
      if (result) {
        await updateSecurityConsolidation({
          dealId: result.dealId,
          securityId: row.security_id,
          roundName: result.roundName
        });
      }
    },
    [openPrompt, updateSecurityConsolidation, refetchDeals, dealList]
  );

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleDateRangeChange = useCallback(
    (value: DateRangeValue) => {
      setFilters((prev) => ({
        ...prev,
        dateRange: value
      }));
      setPage(0);
    },
    [setFilters]
  );

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hasActiveFilters = useMemo(
    () => !!Object.keys(filters.filters).length || !!searchValue,
    [filters.filters, searchValue]
  );

  const showEmptyState = !rows.length && !hasActiveFilters;

  return (
    <Stack sx={{ flexGrow: 1, overflow: "hidden", p: "16px", backgroundColor: "#FFFFFF", borderRadius: "8px" }}>
      {showEmptyState ? (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.securitiesTable.title}
          description={{
            text: EMPTY_CARD_CONTENT.securitiesTable.description,
            externalText: EMPTY_CARD_CONTENT.securitiesTable.externalDescription
          }}
          image={{
            src: EmptyCardDataOperationsSecurities,
            sx: { width: 315, height: 140 }
          }}
          fitPage
        />
      ) : (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" pb="16px">
            <InlineFilter
              handleChangeFilter={(filters) => setFilters((current) => ({ ...current, filters }))}
              filtersList={filtersList}
              applyMode={true}
              state={filters.filters}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <SelectDateRange value={filters.dateRange} onChange={handleDateRangeChange} />
              <Search value={searchValue} onChange={handleChange} placeholder="Search" />
            </Stack>
          </Stack>
          <TableContainer
            component={Paper}
            sx={{ border: "1px solid #DBE8F5", borderRadius: "8px", boxShadow: "none", flexGrow: 1 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ height: 40 }}>
                  <HeaderCell sx={{ maxWidth: 150 }}>Altius Security Name</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 150 }}>Institution Stock Name</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 150 }}>Asset Class</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>ISIN</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>Investor Name</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>Institution</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>Account Number</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>Branch Number</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 100 }}>Units</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>Market Price</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>NAV</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>P&L</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>TWR</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>Initial Date</HeaderCell>
                  <HeaderCell sx={{ maxWidth: 120 }}>Effective Date</HeaderCell>
                  <HeaderCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row) => (
                  <SecuritiesTableRow
                    key={row.deal_id}
                    row={row}
                    depth={0}
                    expanded={expanded}
                    onToggle={handleToggle}
                    onSecurityClick={handleSecurityClick}
                    onAssignSecurity={assignSecurity}
                    onUndoSecurity={handleUndoSecurity}
                    onAssetClassChange={onAssetClassChange}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ flexShrink: 0 }}
          />
        </>
      )}
    </Stack>
  );
};
